import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useServerErrorMessage } from '@/components/organisms/FishGroupForms';
import { useCreateStockingCycle } from '@/domain/stockingCycles';
import { now } from '@/utils/date';

import type { BoardCycle } from '../../types';

import {
    makeNewStockingDefaults,
    type NewStockingFormValues,
    toCreateStockingCycleInput,
} from './newStockingForm';
import StockingForm from './StockingForm';

interface NewStockingModalProps {
    clientId: number;
    locationId: number;
    /** Display name for the note; masked in demo mode. */
    locationName: string;
    /**
     * Every cycle at the site. The suggestion and the duplicate check use the real `name`,
     * the ongoing-stocking note the `displayName`.
     */
    cycles: BoardCycle[];
    /** The backend's name for a stocking created today; the prefill for a first stocking. */
    suggestedName?: string | null;
    /** The container mounts the modal per opening, once the cycles are known. */
    isOpen: boolean;
    onClose: () => void;
    onCreated: (cycleId: number | null) => void;
}

export default function NewStockingModal(props: NewStockingModalProps) {
    const { clientId, locationId, locationName, cycles, isOpen, onClose, onCreated } = props;
    const { suggestedName = null } = props;
    const { t } = useTranslation('fishGroups');
    const names = useMemo(() => cycles.map((cycle) => cycle.name), [cycles]);
    const ongoing = cycles.find((cycle) => cycle.status === 'active');
    const defaults = useMemo(
        () => makeNewStockingDefaults(now(), names, suggestedName),
        [names, suggestedName]
    );
    const { create, error, isLoading, reset } = useCreateStockingCycle(clientId, locationId);
    const serverError = useServerErrorMessage(error);

    const close = () => {
        if (isLoading) return;
        reset();
        onClose();
    };

    const onSubmit = async (submitted: NewStockingFormValues) => {
        const input = toCreateStockingCycleInput(submitted, now());
        if (!input) return;
        const cycleId = await create(input).catch(() => undefined);
        if (cycleId === undefined) return;
        ToastInfo({
            content: t('newStocking.successToast', { name: input.name }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'stocking-cycle-create-success', autoClose: 5000 },
        });
        reset();
        onCreated(cycleId);
    };

    const body = (
        <StockingForm
            defaults={defaults}
            followName={defaults.name}
            takenNames={names}
            submitLabel={t('newStocking.submit')}
            note={
                ongoing
                    ? t('newStocking.ongoingNote', {
                          site: locationName,
                          name: ongoing.displayName,
                      })
                    : t('newStocking.plannedNote')
            }
            serverError={serverError}
            onCancel={close}
            onSubmit={onSubmit}
        />
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={close}
            title={t('newStocking.title')}
            body={body}
            backdrop="static"
        />
    );
}
